// src/Dashboards/Charts.js

import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import "./Charts.css";

const COLORS = {
  Diesel: "#9abfef",
  Petrol: "#FFA500",
  CNG: "#92c022",
  "Speed Petrol": "#7a4a9b",
  LPG: "#ae381c",
  Electric: "#389f81",
};

const getMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getSortedBarData = (groupedData, range, pastDate, today) => {
  const fuelTypes = ["Diesel", "Petrol", "CNG", "Speed Petrol", "LPG", "Electric"];

  if (range === "1week") {
    const barData = [];
    for (let i = 6; i >= 0; i--) {
      const dateLocal = new Date();
      dateLocal.setDate(dateLocal.getDate() - i);
      const dateUTC = new Date(Date.UTC(dateLocal.getFullYear(), dateLocal.getMonth(), dateLocal.getDate()));
      const key = dateUTC.toISOString().split("T")[0];
      const label = dateLocal.toLocaleDateString("en-IN", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

      const dayData = { name: label };
      fuelTypes.forEach((type) => {
        dayData[type] = groupedData[key]?.[type] || 0;
      });

      barData.push(dayData);
    }
    return barData;
  }

  if (range === "1month") {
    const barData = [];
    const past = new Date(today);
    //past.setDate(today.getDate() - 27);
past.setDate(1);

    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(past);
      weekStart.setDate(past.getDate() + i * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      if (weekEnd > today) weekEnd.setTime(today.getTime());

      const label = `${weekStart.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} - ${weekEnd.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`;
      const weekData = { name: label };
      fuelTypes.forEach((type) => (weekData[type] = 0));

      const startISO = weekStart.toISOString().split("T")[0];
      const endISO = weekEnd.toISOString().split("T")[0];

      for (const dateStr in groupedData) {
        if (dateStr >= startISO && dateStr <= endISO) {
          const fuels = groupedData[dateStr];
          fuelTypes.forEach((type) => {
            weekData[type] += fuels[type] || 0;
          });
        }
      }

      barData.push(weekData);
    }

    return barData;
  }

    const allMonths = [];
  const endMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthsToInclude = range === "3month" ? 3 : 6;

  for (let i = monthsToInclude - 1; i >= 0; i--) {
    const month = new Date(
      endMonth.getFullYear(),
      endMonth.getMonth() - i,
      1
    );
    allMonths.push(month);
  }

  return allMonths.map((month) => {
  const key = getMonthKey(month);
  const label = month.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });

  const monthData = { name: label };

  fuelTypes.forEach((type) => {
    monthData[type] = groupedData[key]?.[type] || 0;
  });

  return monthData;
});
};

const Charts = ({ data, selectedRange, visibleFuels, setAvailableFuels }) => {
  const [pieData, setPieData] = useState([]);
  const [barData, setBarData] = useState([]);
  const [localAvailableFuels, setLocalAvailableFuels] = useState([]);
  const selectedRoId = localStorage.getItem("selectedRoId");

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const today = new Date();
        const pastDate = new Date(today);
        if (selectedRange === "1week") pastDate.setDate(today.getDate() - 6);
        else if (selectedRange === "1month") pastDate.setMonth(today.getMonth() - 1);
        else if (selectedRange === "3month") pastDate.setMonth(today.getMonth() - 3);
        else if (selectedRange === "6month") pastDate.setMonth(today.getMonth() - 6);
        pastDate.setHours(0, 0, 0, 0);

        const filteredData = data.filter((item) => item.ro_id === selectedRoId);
        const presentFuels = [...new Set(filteredData.map((item) => item.fueltype).filter(Boolean))];
        setLocalAvailableFuels(presentFuels);
        setAvailableFuels?.(presentFuels);

        const fuelTypes = ["Diesel", "Petrol", "CNG", "Speed Petrol", "LPG", "Electric"];

        const aggregatedPie = fuelTypes
          .filter((type) => presentFuels.includes(type))
          .map((type) => {
            const total = filteredData
              .filter((item) => item.fueltype?.toLowerCase() === type.toLowerCase())
              .reduce((sum, entry) => sum + parseFloat(entry.quantity || 0), 0);
            return { name: type, value: total };
          });
        setPieData(aggregatedPie);

        const groupedData = {};
        filteredData.forEach((entry) => {
          const date = new Date(entry.date);
          const fuelType = entry.fueltype;
          if (!fuelType) return;

          let key = "";
          if (selectedRange === "1week" || selectedRange === "1month") {
            const [day, month, year] = date.toLocaleDateString("en-IN").split("/").map(Number);
            key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          } else {
           key = getMonthKey(date); // "2024-09"

          }

          if (!groupedData[key]) groupedData[key] = {};
          groupedData[key][fuelType] = (groupedData[key][fuelType] || 0) + parseFloat(entry.quantity || 0);
        });

        setBarData(getSortedBarData(groupedData, selectedRange, pastDate, today));
      } catch (error) {
        console.error("Error fetching chart data:", error);
        setPieData([]);
        setBarData([]);
        setLocalAvailableFuels([]);
        setAvailableFuels?.([]);
      }
    };

    fetchChartData();
  }, [data, selectedRange, visibleFuels, setAvailableFuels, selectedRoId]);

  const filteredBarData = barData.map((entry) => {
    const filteredEntry = { ...entry };
    Object.keys(entry).forEach((key) => {
      if (key !== "name" && visibleFuels[key] === false) {
        filteredEntry[key] = 0;
      }
    });
    return filteredEntry;
  });

  return (
    <div className="charts-container">
      <div className="chart-box bar-chart" style={{ width: "75%" }}>
        <h3 style={{ color: "#064a52" }}>Fuel Quantity</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredBarData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }} barCategoryGap="50%" barGap={0}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" interval={0} angle={-30} textAnchor="end" height={60} type="category" tick={{ fontSize: 10 }} />
            <YAxis />
            <Tooltip
              formatter={(value, name) => {
                const unit = name === "CNG" || name === "Electric" ? "KG" : "L";
                return [`${parseFloat(value).toFixed(2)} ${unit}`, name];
              }}
            />
            <Legend />
            {localAvailableFuels.map((fuel) => (
              visibleFuels[fuel === "Speed Petrol" ? "SpeedPetrol" : fuel] && (
                <Bar key={fuel} dataKey={fuel} fill={COLORS[fuel]} barSize={15} />
              )
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="chart-box pie-chart" style={{ width: "25%" }}>
        <h3 style={{ marginBottom: "8px", color: "#064a52" }}>Fuel Type Distribution</h3>
        <div style={{ display: "flex", flexDirection: "row", height: "150px" }}>
          <div style={{ width: "50%" }}>
            <PieChart width={250} height={250}>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={60} fill="#82ca9d" dataKey="value">
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => {
                  if (value > 0) {
                    const unit = name === "CNG" || name === "Electric" ? "KG" : "L";
                    return [`${parseFloat(value).toFixed(2)} ${unit}`, name];
                  }
                  return ["", ""];
                }}
              />
            </PieChart>
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", paddingLeft: "12px" }}>
            {pieData.map((entry) => (
              <div key={entry.name} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: COLORS[entry.name], marginRight: 8 }} />
                <span style={{ fontSize: "14px", fontWeight: 500 }}>{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charts;
